import React from 'react';
import styled from 'styled-components';
import { FaWifi, FaStar, FaProjectDiagram, FaChalkboard, FaCar, FaPhone, FaSwimmer, FaDog, FaBicycle, FaTv, FaCoffee, FaUtensils, FaFan, FaFireExtinguisher, FaShower, FaCouch, FaBed, FaHotTub, FaLeaf, FaLock, FaSuitcase, FaTshirt, FaHiking, FaUmbrellaBeach, FaMusic, FaHandHoldingHeart, FaLightbulb, FaTree, FaBaby, FaChair, FaSpa, FaConciergeBell, FaDoorOpen, FaThermometer, FaWind, FaChargingStation } from 'react-icons/fa';
import Reservation from './Reservation';
import { useTranslation } from "react-i18next";

// Styled Components

const DetailsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  width: 100%;
  margin-top: 30px;
  font-family: 'Arial', sans-serif;
`;

const LeftColumn = styled.div`
  padding: 0px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const RightColumn = styled.div`
  padding: 0px;
  align-items: center;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: bold;
  margin: 0;
  color: #333;
`;

const Subtitle = styled.p`
  color: #717171;
  font-size: 1.125rem;
  margin: 1px 0 0 0;
`;

const StarContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin: 5px 15px 20px 0;
`;

const HostContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const HostImage = styled.img`
  margin: 20px 0 0 0;
  width: 60px;
  height: 60px;
  border-radius: 50%;
`;

const HostDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0px;
`;

const HostName = styled.p`
  font-weight: bold;
  color: #333;
`;

const HostInfo = styled.p`
  color: #717171;
  margin: 0 0 0 0;
`;

const AmenitiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  margin-top: 20px;
`;

const FullWidth = styled.div`
  grid-column: 1 / 3;
`;

const AmenityContainer = styled.div`
  display: flex;
  align-items: center;
`;

const AmenityIcon = styled.div`
  font-size: 1.5rem;
  margin-right: 10px;
  color: #333;
`;

const AmenityText = styled.p`
  color: #333;
  font-size: 1rem;
`;

const ReviewTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: bold;
  color: #333;
  margin-top: 30px;
`;

const ReviewContainer = styled.div`
  margin-top: 20px;
`;

const Review = styled.div`
  border-bottom: 1px solid #ddd;
  padding-bottom: 15px;
  margin-bottom: 15px;
`;

const ReviewText = styled.p`
  font-size: 1rem;
  color: #555;
`;

const ReviewRating = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
`;

const renderStars = (rating) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = 5 - Math.ceil(rating);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
      {[...Array(fullStars)].map((_, index) => (
        <FaStar key={`full-${index}`} style={{ color: '#ffd700' }} />
      ))}
      {hasHalfStar && <FaStar style={{ color: '#ffd700' }} />}
      {[...Array(emptyStars)].map((_, index) => (
        <FaStar key={`empty-${index}`} style={{ color: '#ddd' }} />
      ))}
    </div>
  );
};

const ListingDetails = ({ listing, isAuthenticated}) => {
  const { t } = useTranslation();
  const amenityIcons = {
    WiFi: <FaWifi />,
    "Air Conditioning": <FaFan />,
    Projector: <FaProjectDiagram />,
    "Whiteboard": <FaChalkboard />,
    Parking: <FaCar />,
    "Conference Phone": <FaPhone />,
    "Swimming Pool": <FaSwimmer />,
    "Pet Friendly": <FaDog />,
    "Bicycle Parking": <FaBicycle />,
    TV: <FaTv />,
    "Coffee Maker": <FaCoffee />,
    Restaurant: <FaUtensils />,
    Fireplace: <FaFireExtinguisher />,
    Shower: <FaShower />,
    Sofa: <FaCouch />,
    Bed: <FaBed />,
    "Hot Tub": <FaHotTub />,
    Garden: <FaLeaf />,
    "Secure Access": <FaLock />,
    "Luggage Storage": <FaSuitcase />,
    Laundry: <FaTshirt />,
    "Hiking Trails": <FaHiking />,
    Beachfront: <FaUmbrellaBeach />,
    "Music System": <FaMusic />,
    "Child Friendly": <FaHandHoldingHeart />,
    "Outdoor Lighting": <FaLightbulb />,
    "Nature View": <FaTree />,
    "Baby Crib": <FaBaby />,
    "Dining Area": <FaChair />,
    Spa: <FaSpa />,
    "Room Service": <FaConciergeBell />,
    "Private Entrance": <FaDoorOpen />,
    Heating: <FaThermometer />,
    Fan: <FaWind />,
    "Electric Charging Station": <FaChargingStation />,
  };
  const keywords = [
    "Cozy", "Modern", "Spacious", "Luxurious", "Affordable",
    "Office", "Workspace", "Conference Room", "Desk"
  ];

  const translatedName = listing.name
    .split(" ")
    .map(word => keywords.includes(word) ? t(word.toLowerCase().replace(" ", "_")) : word)
    .join(" ");

  return (
    <DetailsContainer>
      {/* Left Column */}
      <LeftColumn>
        <TitleContainer>
          <Title>{translatedName}</Title>
        </TitleContainer>
        <Subtitle>{listing.city}, {listing.country}</Subtitle>
        <StarContainer>{renderStars(listing.rating)}</StarContainer> 
        <HostContainer>
          <HostImage src={listing.owner.picture_url} alt="Host" />
          <HostDetails>
            <HostName>{t('stayWith')} {listing.owner.name}</HostName>
            <HostInfo>{t('memberSince')} {new Date(listing.owner.member_since).toLocaleDateString()}</HostInfo>
          </HostDetails>
        </HostContainer>
        <AmenitiesGrid>
          {listing.amenities?.map((amenity, index) => (
            <AmenityContainer key={index}>
              <AmenityIcon>{amenityIcons[amenity]}</AmenityIcon>
              <AmenityText>{amenity}</AmenityText>
            </AmenityContainer>
          ))}
        </AmenitiesGrid>
      </LeftColumn>

      {/* Right Column */}
      <RightColumn>{<Reservation id={listing.id} price={listing.price} reservations={listing.reservations} maxOccupants={listing.occupants} isAuthenticated={isAuthenticated}/>}</RightColumn>

      {/* Full Width: Reviews */}
      <FullWidth>
        <ReviewTitle>{t('reviews')}</ReviewTitle>
        <ReviewContainer>
          {listing.reviews?.length > 0 ? (
            listing.reviews.map((review, index) => (
              <Review key={index}>
                <ReviewRating>
                  {renderStars(review.rating)}
                  <span>{review.user_name}</span>
                </ReviewRating>
                <ReviewText>{review.review_text}</ReviewText>
              </Review>
            ))
          ) : (
            <p>{t('noReviewsAvailable')}.</p>
          )}
        </ReviewContainer>
      </FullWidth>
    </DetailsContainer>
  );
};

export default ListingDetails;
